'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Search, MapPin, Briefcase, Building2, TrendingUp, Download, Calculator, Phone, Mail, ArrowRight, ChevronRight, Bed, Bath, Maximize2, Building, DollarSign, Users, BarChart3 } from 'lucide-react';

interface Property {
  id: string;
  title: string;
  slug: string;
  price: number;
  listing_type: string;
  city?: string;
  bedrooms?: number;
  bathrooms?: number;
  floor_area?: number;
  primary_image_url?: string;
  images_urls?: string[];
  property_type?: string;
}

export default function CommercialPage() {
  const router = useRouter();
  const [properties, setProperties] = useState<Property[]>([]);
  const [totalProperties, setTotalProperties] = useState(0);
  const [loading, setLoading] = useState(true);
  const [listingType, setListingType] = useState<'sale' | 'rent'>('sale');
  const [searchLocation, setSearchLocation] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [minSize, setMinSize] = useState('');
  const [priceRange, setPriceRange] = useState('');

  useEffect(() => {
    console.log('=== FETCHING COMMERCIAL PROPERTIES ===');
    console.log('Listing Type State:', listingType);

    // Fetch ONLY commercial properties from API using property_type filter
    const listingTypeParam = listingType === 'sale' ? 'For Sale' : 'For Rent';
    const propertyTypes = 'commercial,office_space,warehouse,agricultural';

    console.log('API Params:', { listing_type: listingTypeParam, property_type: propertyTypes });

    fetch(`/api/properties?property_type=${propertyTypes}&listing_type=${encodeURIComponent(listingTypeParam)}&limit=12`)
      .then(res => res.json())
      .then(data => {
        console.log('=== API RESPONSE ===');
        const commercialProps = data.data?.properties || data.properties || [];
        console.log('Total commercial properties from API:', commercialProps.length);
        console.log('Total available:', data.data?.pagination?.total || data.data?.total || 0);

        // Property type breakdown
        if (commercialProps.length > 0) {
          console.log('Property type breakdown:', commercialProps.reduce((acc: any, p: any) => {
            const type = p.property_type || 'unknown';
            acc[type] = (acc[type] || 0) + 1;
            return acc;
          }, {}));

          console.log('Sample properties:', commercialProps.slice(0, 3).map((p: any) => ({
            title: p.title,
            type: p.property_type,
            listing: p.listing_type,
            price: p.price
          })));
        }

        // Store total count from pagination and display all returned properties
        setTotalProperties(data.data?.pagination?.total || 0);
        setProperties(commercialProps);
        setLoading(false);
      })
      .catch(err => {
        console.error('=== API ERROR ===', err);
        setLoading(false);
      });
  }, [listingType]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    params.append('listing_type', listingType === 'sale' ? 'For Sale' : 'For Rent');
    if (searchLocation) params.append('search', searchLocation);
    if (propertyType) params.append('property_type', propertyType);
    if (minSize) params.append('min_floor_area', minSize);
    if (priceRange) {
      const [min, max] = priceRange.split('-');
      params.append('min_price', min);
      if (max && !max.includes('+')) params.append('max_price', max);
    }
    router.push(`/properties?${params.toString()}`);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Search Focused (Industry Standard) */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-32 pb-20">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} />

        <div className="container relative max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <div className="inline-block px-4 py-1.5 bg-white/10 text-white text-xs font-medium tracking-wider uppercase mb-6">
              Commercial Real Estate
            </div>

            <h1 className="text-5xl md:text-6xl font-light text-white mb-6 leading-tight">
              Office, Industrial &<br />Investment Properties
            </h1>
            <p className="text-xl text-slate-300 font-light mb-12">
              Professional commercial space for {listingType === 'sale' ? 'sale' : 'lease'} across Metro Manila
            </p>

            {/* Advanced Search Box with Integrated Tabs */}
            <div className="bg-white rounded-sm shadow-2xl">
              {/* Sale/Rent Tabs - Integrated */}
              <div className="border-b border-gray-200">
                <div className="flex">
                  <button
                    onClick={() => setListingType('sale')}
                    className={`flex-1 py-4 text-sm font-semibold tracking-wider uppercase transition-all ${
                      listingType === 'sale'
                        ? 'bg-slate-900 text-white'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    For Sale
                  </button>
                  <button
                    onClick={() => setListingType('rent')}
                    className={`flex-1 py-4 text-sm font-semibold tracking-wider uppercase transition-all ${
                      listingType === 'rent'
                        ? 'bg-slate-900 text-white'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    For Rent
                  </button>
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-4 p-6">
                {/* Location */}
                <div className="md:col-span-1">
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    placeholder="City or Address"
                    className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:border-gray-900"
                    value={searchLocation}
                    onChange={(e) => setSearchLocation(e.target.value)}
                  />
                </div>

                {/* Property Type */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                    Property Type
                  </label>
                  <select
                    className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:border-gray-900"
                    value={propertyType}
                    onChange={(e) => setPropertyType(e.target.value)}
                  >
                    <option value="">All Types</option>
                    <option value="office">Office Space</option>
                    <option value="commercial">Commercial Buildings</option>
                    <option value="warehouse">Warehouse/Industrial</option>
                    <option value="hotel">Hotels</option>
                    <option value="land">Commercial Lots</option>
                    <option value="resort">Resorts & Beach Lots</option>
                  </select>
                </div>

                {/* Size */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                    Min Size (sqm)
                  </label>
                  <select
                    className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:border-gray-900"
                    value={minSize}
                    onChange={(e) => setMinSize(e.target.value)}
                  >
                    <option value="">Any Size</option>
                    <option value="50">50+</option>
                    <option value="100">100+</option>
                    <option value="200">200+</option>
                    <option value="500">500+</option>
                    <option value="1000">1,000+</option>
                  </select>
                </div>

                {/* Price Range */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                    Price Range
                  </label>
                  <select
                    className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:border-gray-900"
                    value={priceRange}
                    onChange={(e) => setPriceRange(e.target.value)}
                  >
                    <option value="">Any Price</option>
                    <option value="0-5000000">Up to ₱5M</option>
                    <option value="5000000-10000000">₱5M - ₱10M</option>
                    <option value="10000000-50000000">₱10M - ₱50M</option>
                    <option value="50000000-100000000">₱50M - ₱100M</option>
                    <option value="100000000+">₱100M+</option>
                  </select>
                </div>
              </div>

              <button
                onClick={handleSearch}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white py-4 text-sm font-semibold tracking-wider uppercase transition-colors"
              >
                Search Properties
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Property Type Cards - Data Focused */}
      <section className="py-20 bg-slate-50">
        <div className="container max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Office */}
            <Link href="/properties?property_type=office" className="group bg-white border border-gray-200 hover:border-slate-900 transition-all">
              <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                <img
                  src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&q=80"
                  alt="Office Space"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-5 h-5 text-slate-900" />
                  <h3 className="text-xl font-semibold">Office</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Grade A & B office spaces in prime business districts
                </p>
                <div className="flex items-center justify-between pt-4 border-t">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">View Listings</span>
                  <ArrowRight className="w-4 h-4 text-slate-900 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            {/* Industrial */}
            <Link href="/properties?property_type=warehouse" className="group bg-white border border-gray-200 hover:border-slate-900 transition-all">
              <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                <img
                  src="https://images.unsplash.com/photo-1553413077-190dd305871c?w=600&q=80"
                  alt="Warehouse"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Building className="w-5 h-5 text-slate-900" />
                  <h3 className="text-xl font-semibold">Industrial</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Warehouses and logistics facilities with loading access
                </p>
                <div className="flex items-center justify-between pt-4 border-t">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">View Listings</span>
                  <ArrowRight className="w-4 h-4 text-slate-900 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            {/* Retail */}
            <Link href="/properties?property_type=commercial" className="group bg-white border border-gray-200 hover:border-slate-900 transition-all">
              <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                <img
                  src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&q=80"
                  alt="Retail Space"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Briefcase className="w-5 h-5 text-slate-900" />
                  <h3 className="text-xl font-semibold">Retail</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  High-traffic commercial locations for retail business
                </p>
                <div className="flex items-center justify-between pt-4 border-t">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">View Listings</span>
                  <ArrowRight className="w-4 h-4 text-slate-900 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Properties - Data Display */}
      <section className="py-20 bg-white">
        <div className="container max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Featured Listings</div>
              <h2 className="text-4xl font-light">Available Properties</h2>
              <p className="text-sm text-gray-500 mt-2">
                Showing {properties.length} of {totalProperties} properties for {listingType === 'sale' ? 'sale' : 'rent'}
              </p>
            </div>
            <Link href="/properties?property_type=office,warehouse,commercial" className="text-sm font-medium uppercase tracking-wider hover:underline">
              View All →
            </Link>
          </div>

          {(() => {
            console.log('=== FEATURED PROPERTIES RENDER ===');
            console.log('Loading:', loading);
            console.log('Properties count:', properties.length);
            console.log('Properties data:', properties);
            return null;
          })()}

          {loading ? (
            <div className="text-center py-20">
              <p className="text-gray-500">Loading properties...</p>
              <p className="text-xs text-gray-400 mt-2">Check browser console for details</p>
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-20 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg">
              <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg mb-2">No commercial properties available for {listingType === 'sale' ? 'sale' : 'rent'}</p>
              <p className="text-sm text-gray-500">Check browser console (F12) to see API response</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {properties.map((property) => (
                <Link
                  key={property.id}
                  href={`/property/${property.slug}`}
                  className="group bg-white border border-gray-200 hover:border-slate-900 transition-all"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                    {property.primary_image_url || (property.images_urls && property.images_urls.length > 0) ? (
                      <img
                        src={property.primary_image_url || property.images_urls?.[0] || ''}
                        alt={property.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                        <Building2 className="w-16 h-16 text-gray-400" />
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-1 bg-slate-900 text-white text-[10px] font-semibold uppercase tracking-wider">
                        {property.listing_type || 'For Lease'}
                      </span>
                      {property.property_type && (
                        <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                          {property.property_type}
                        </span>
                      )}
                    </div>

                    <h3 className="text-lg font-semibold mb-2 line-clamp-2 group-hover:text-slate-900">
                      {property.title}
                    </h3>

                    <p className="text-sm text-gray-500 flex items-center gap-1.5 mb-4">
                      <MapPin className="w-3.5 h-3.5" />
                      {property.city || 'Metro Manila'}
                    </p>

                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="text-2xl font-bold text-slate-900">
                        {formatPrice(property.price)}
                      </span>
                      {property.listing_type === 'For Rent' && (
                        <span className="text-sm text-gray-500">/month</span>
                      )}
                    </div>

                    {property.floor_area && (
                      <div className="pt-4 border-t">
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                          <Maximize2 className="w-4 h-4" />
                          <span className="font-medium">{property.floor_area} sqm</span>
                        </div>
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Investment Services - Data Driven */}
      <section className="py-20 bg-slate-50">
        <div className="container max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Professional Services</div>
            <h2 className="text-4xl font-light mb-4">Commercial Real Estate Solutions</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Expert guidance for investors, businesses, and property owners
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-white border-2 border-slate-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-7 h-7 text-slate-900" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Investment Analysis</h3>
              <p className="text-sm text-gray-600">
                Cap rate, NOI, and ROI calculations for informed decisions
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white border-2 border-slate-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-7 h-7 text-slate-900" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Market Research</h3>
              <p className="text-sm text-gray-600">
                Comprehensive market data and trend analysis
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white border-2 border-slate-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-slate-900" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Tenant Representation</h3>
              <p className="text-sm text-gray-600">
                Finding the right space for your business needs
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white border-2 border-slate-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-7 h-7 text-slate-900" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Property Valuation</h3>
              <p className="text-sm text-gray-600">
                Professional appraisal and valuation services
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA - Professional */}
      <section className="py-20 bg-white">
        <div className="container max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-light mb-6">Ready to Discuss Your Requirements?</h2>
          <p className="text-lg text-gray-600 mb-12">
            Our commercial real estate specialists are available to assist with property search, investment analysis, and transaction support.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href="/list-property"
              className="inline-flex items-center gap-2 px-8 py-4 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold uppercase tracking-wider transition-colors"
            >
              List Your Property
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="tel:+639602535917"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-slate-900 hover:bg-slate-900 hover:text-white text-slate-900 text-sm font-semibold uppercase tracking-wider transition-all"
            >
              <Phone className="w-4 h-4" />
              Call Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
